// Unique ID creation requires a high quality random # generator. In the browser we therefore
// require the crypto API and do not support built-in fallback to lower quality random number
// generators (like Math.random()).
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  // lazy load so that environments that need to polyfill have a chance to do so
  if (!getRandomValues) {
    // getRandomValues needs to be invoked in a context where "this" is a Crypto implementation.
    getRandomValues = typeof crypto !== 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);

    if (!getRandomValues) {
      throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
    }
  }

  return getRandomValues(rnds8);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

const byteToHex = [];

for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).slice(1));
}

function unsafeStringify(arr, offset = 0) {
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}

const randomUUID = typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID.bind(crypto);
const native = {
  randomUUID
};

function v4(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }

  options = options || {};
  const rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  if (buf) {
    offset = offset || 0;

    for (let i = 0; i < 16; ++i) {
      buf[offset + i] = rnds[i];
    }

    return buf;
  }

  return unsafeStringify(rnds);
}

const Api = ({ authToken, apiOrigin }) => {
    async function getAuthorizationHeader() {
        if (!authToken) {
            return {
                'Content-Type': 'application/json',
            };
        }
        return {
            Authorization: `Bearer ${authToken}`,
            'Idempotency-Key': v4(),
            'Content-Type': 'application/json',
        };
    }
    async function makeRequest(endpoint, method, params, body, signal, headers) {
        const url = `${apiOrigin}/v1/${endpoint}`;
        const requestUrl = params ? `${url}?${new URLSearchParams(params)}` : url;
        const defaultHeaders = await getAuthorizationHeader();
        const mergedHeaders = Object.assign(Object.assign({}, defaultHeaders), headers);
        const response = await fetch(requestUrl, {
            method: method,
            headers: mergedHeaders,
            body: body,
            signal: signal,
        });
        if (response) {
            return response.status === 204 ? {} : response.json();
        }
        handleError(requestUrl);
    }
    async function get(endpoint, params, signal, headers) {
        return makeRequest(endpoint, 'GET', params, null, signal, headers);
    }
    async function post(endpoint, body, params, signal) {
        return makeRequest(endpoint, 'POST', params, JSON.stringify(body), signal);
    }
    async function put(endpoint, body, params, signal) {
        return makeRequest(endpoint, 'PUT', params, JSON.stringify(body), signal);
    }
    async function patch(endpoint, body, params, signal) {
        return makeRequest(endpoint, 'PATCH', params, JSON.stringify(body), signal);
    }
    async function destroy(endpoint, params, signal) {
        return makeRequest(endpoint, 'DELETE', params, null, signal);
    }
    return { get, post, put, patch, destroy };
};
function handleError(requestUrl) {
    console.error(`Error fetching from ${requestUrl}`);
}

export { Api as A };
